// src/services/clientes.ts
/**
 * Service para operaciones de Clientes.
 */
import { api } from './api';
import type { Cliente, ClienteCreate, ClienteUpdate, Empresa, EmpresaCreate, Contacto, ContactoCreate } from '@/types';
import type { PaginatedResponse, FilterParams } from '@/types';

// Tipos específicos de filtros
interface ClienteFilters extends FilterParams {
  activo?: boolean;
}

// Clientes
export const clientesService = {
  // Listar clientes con paginación
  list: (params?: ClienteFilters) => {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.set('search', params.search);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.page_size) searchParams.set('page_size', params.page_size.toString());
    if (params?.activo !== undefined) searchParams.set('activo', params.activo.toString());
    if (params?.sort_by) searchParams.set('sort_by', params.sort_by);
    if (params?.sort_order) searchParams.set('sort_order', params.sort_order);

    const query = searchParams.toString();
    return api.get<PaginatedResponse<Cliente>>(`/clientes${query ? `?${query}` : ''}`);
  },

  // Obtener cliente por ID
  get: (id: string) => api.get<Cliente>(`/clientes/${id}`),

  // Crear cliente
  create: (data: ClienteCreate) => api.post<Cliente>('/clientes', data),

  // Actualizar cliente
  update: (id: string, data: ClienteUpdate) => api.patch<Cliente>(`/clientes/${id}`, data),

  // Eliminar cliente (soft delete)
  delete: (id: string) => api.delete<void>(`/clientes/${id}`),

  // Empresas del cliente
  getEmpresas: (clienteId: string) => api.get<Empresa[]>(`/clientes/${clienteId}/empresas`),

  createEmpresa: (clienteId: string, data: EmpresaCreate) =>
    api.post<Empresa>(`/clientes/${clienteId}/empresas`, data),

  updateEmpresa: (clienteId: string, empresaId: string, data: Partial<EmpresaCreate>) =>
    api.patch<Empresa>(`/clientes/${clienteId}/empresas/${empresaId}`, data),

  deleteEmpresa: (clienteId: string, empresaId: string) =>
    api.delete<void>(`/clientes/${clienteId}/empresas/${empresaId}`),

  // Contactos — todos (para catálogo fuzzy)
  getAllContactos: (limit: number = 250) => api.get<Contacto[]>(`/contactos?limit=${limit}`),

  // Contactos del cliente
  getContactos: (clienteId: string) => api.get<Contacto[]>(`/clientes/${clienteId}/contactos`),

  createContacto: (clienteId: string, data: ContactoCreate) =>
    api.post<Contacto>(`/clientes/${clienteId}/contactos`, data),

  updateContacto: (clienteId: string, contactoId: string, data: Partial<ContactoCreate>) =>
    api.patch<Contacto>(`/clientes/${clienteId}/contactos/${contactoId}`, data),

  deleteContacto: (clienteId: string, contactoId: string) =>
    api.delete<void>(`/clientes/${clienteId}/contactos/${contactoId}`),
};
